<script setup>
/**
 * Coach-mark tour overlay — a port of the prototype's `LvlTour`.
 *
 * Presentation only: every step, the storage and all the geometry live in the shared
 * `levellingTour` composable, so beta can reuse the behaviour and only supply markup.
 *
 * Escape skips, and a resize or a scroll of the underlying workspace re-measures — the
 * ring is positioned in viewport coordinates, so anything that moves the target has to
 * move the ring with it or it points at empty space.
 */
import { onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  /** The levellingTour() self object — MUST be markRaw'd by the host (Vue 2.7). */
  tour: { type: Object, required: true },
});

/* Read the prop at call time, never a copy taken at setup. framework.vue swaps `tour`
   between tabs without remounting this, and a captured copy left the handlers driving
   whichever tour happened to be current on mount. */
function onKey(e) {
  // Only while open: close() also marks the tour done, and Escape is pressed for
  // plenty of other reasons (dialogs, menus) on the same page.
  if (e.key === 'Escape' && props.tour.open.value) props.tour.close();
}
function onMove() {
  props.tour.measure();
}

onMounted(() => {
  window.addEventListener('keydown', onKey);
  window.addEventListener('resize', onMove);
  /* Capture phase: the workspace scrolls an inner element, not the window, and scroll
     events do not bubble. Without capture the ring stays put while the target moves. */
  window.addEventListener('scroll', onMove, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('resize', onMove);
  window.removeEventListener('scroll', onMove, true);
});
</script>

<template>
  <div v-if="tour.open.value && tour.step.value" class="lvl-tour" role="dialog" aria-modal="true" aria-label="Product tour">
    <!-- The ring IS the scrim: a 9999px spread shadow dims everything outside it, so one
         element both highlights and masks. A concept step with no target falls back to a
         plain dim. -->
    <div v-if="tour.ringStyle.value" class="lvl-tour-ring" :style="tour.ringStyle.value"></div>
    <div v-else class="lvl-tour-dim"></div>

    <div class="lvl-tour-card" :style="tour.cardStyle.value">
      <button class="lvl-tour-x" aria-label="Skip tour" @click="tour.close()">
        <v-icon size="16">mdi-close</v-icon>
      </button>
      <div class="lvl-tour-kicker">
        <v-icon size="14">mdi-school-outline</v-icon> Getting started
      </div>
      <h4>{{ tour.step.value.title }}</h4>
      <p>{{ tour.step.value.body }}</p>
      <div class="lvl-tour-foot">
        <span class="lvl-tour-n">{{ tour.index.value + 1 }} of {{ tour.count }}</span>
        <span class="lvl-tour-btns">
          <button v-if="tour.index.value > 0" class="lvl-tour-back" @click="tour.back()">Back</button>
          <button class="lvl-tour-next" @click="tour.next()">
            {{ tour.isLast.value ? 'Done' : 'Next' }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Ported from the prototype's .lvl-tour rules (lvl/lvl.css) against the --cc-* tokens
   theme.scss declares globally for light and dark. */
.lvl-tour {
  position: fixed;
  inset: 0;
  /* Above the app chrome, below nothing else on this surface. */
  z-index: 1300;
}

.lvl-tour-dim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
}

/* One element does both jobs: the huge spread dims the page, the 2px ring highlights.
   pointer-events: none so the spotlight never eats a click meant for the card. */
.lvl-tour-ring {
  position: fixed;
  border-radius: 10px;
  box-shadow:
    0 0 0 9999px rgba(15, 23, 42, 0.45),
    0 0 0 2px var(--cc-accent, #1d5bd6);
  transition: all 0.25s ease;
  pointer-events: none;
}

.lvl-tour-card {
  position: fixed;
  padding: 16px 18px 14px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 12px;
  background: var(--cc-surface, #fff);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
  transition: left 0.25s ease, top 0.25s ease;

  h4 {
    margin: 0 0 6px;
    font-size: 14.5px;
    font-weight: 700;
    color: var(--cc-text, #1f242d);
  }
  p {
    margin: 0 0 14px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--cc-text-2, #525c6b);
    text-wrap: pretty;
  }
}

.lvl-tour-kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--cc-accent, #1d5bd6);

  .v-icon { color: inherit; }
}

.lvl-tour-foot { display: flex; align-items: center; justify-content: space-between; }
.lvl-tour-n {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  font-variant-numeric: tabular-nums;
}
.lvl-tour-btns { display: inline-flex; gap: 8px; }

.lvl-tour-back {
  padding: 5px 12px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 8px;
  background: transparent;
  color: var(--cc-text-2, #525c6b);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: var(--cc-canvas, #f6f7f9); color: var(--cc-text, #1f242d); }
}

.lvl-tour-next {
  padding: 5px 14px;
  border: 0;
  border-radius: 8px;
  background: var(--cc-accent, #1d5bd6);
  color: #fff;
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;

  &:hover { filter: brightness(1.06); }
}

.lvl-tour-x {
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;

  .v-icon { color: inherit; }
  &:hover { background: var(--cc-canvas, #f6f7f9); color: var(--cc-text, #1f242d); }
}
</style>
